import React, { useContext, useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Box } from "@mui/material";
import { formatDate } from "features/helpers/utils";
import { analysisColors } from "features/configure/chartColors";
import Percent from "components/Percent/Percent";
import { useOutletContext } from "react-router";
import { KeyMatrixContext } from "../context/KeyMatrixProvider";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import axios from "axios";
import { apiUrl } from "features/configure";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import { useIntl } from "react-intl";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import { Loading } from "features/common";

export default function ObjectivesBreakdownChart({
  dynamicApiUrl,
  dynamicEntityName,
}) {
  const [objectivesBreakdown, setObjectivesBreakdown] = useState(null);
  const [entityName] = useOutletContext();
  const { granularity, range, formatLabel, isLoading, setIsLoading } =
    useContext(KeyMatrixContext);
  const dispatchSnackbar = useDispatchSnackbar();
  const { formatMessage } = useIntl();
  async function fetchObjectivesBreakdown() {
    try {
      setIsLoading((prev) => ({ ...prev, objectivesBreakdownChart: true }));

      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dashboard/${dynamicEntityName}/objectivesBreakdown`,
        {
          granularity,
          range,
        }
      );
      setObjectivesBreakdown(data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsLoading((prev) => ({ ...prev, objectivesBreakdownChart: false }));
    }
  }
  useEffect(() => {
    fetchObjectivesBreakdown();
  }, [granularity, range]);

  const xAxisData = objectivesBreakdown?.data?.map((item) =>
    formatDate(item?.date, { month: true })
  );

  const types = objectivesBreakdown
    ? Object.keys(objectivesBreakdown?.currentMetrics)
    : [];
  const seriesData = types?.map((object) => ({
    name: object,
    data: objectivesBreakdown?.data?.map((data) => data[object]),
    type: "line",
    smooth: true,
    showSymbol: false,
  }));

  const stats = types?.map((object) => ({
    title: object,
    value: objectivesBreakdown?.currentMetrics[object],
    growth: objectivesBreakdown?.growthMetrics[object],
  }));

  const option = {
    color: analysisColors.large,
    tooltip: {
      trigger: "axis",
      symbol: "none",
      formatter: (params) => {
        // 假設 x 軸的值是時間字串
        const dataIndex = params[0].dataIndex;
        const originalDate = objectivesBreakdown?.data[dataIndex]?.date;
        const formattedTime = formatLabel(originalDate);
        let tooltipContent = `${formattedTime}<br/>`;
        params.forEach((item) => {
          tooltipContent += `
          <span style="display: inline-block; width: 12px; height: 8px; background-color: ${
            item.color
          }; border-radius: 2px; margin-right: 4px;"></span>
          ${item.seriesName}: ${item.value.toLocaleString()}<br/>
        `;
        });
        return tooltipContent;
      },
    },
    grid: {
      left: "0",
      right: "20px",
      top: "20px",
      bottom: "0",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: xAxisData,
    },
    yAxis: {
      type: "value",
    },
    series: seriesData,
  };

  const isNoData = objectivesBreakdown?.data?.length === 0;

  return (
    <Box bgcolor="white" p="16px" color="#555" borderRadius="4px">
      <Box fontSize={16}>
        {formatMessage({ id: "websiteTraffic.chart.objectiveBreakdown" })}
      </Box>

      <Box sx={{ minHeight: "360px" }}>
        {isLoading.objectivesBreakdownChart ? (
          <Box sx={{ position: "relative", height: "100%" }}>
            <Loading open={true} fullScreen={false} isChart={true} />
          </Box>
        ) : isNoData ? (
          <EmptyIconStatus sx={{ height: "100%" }} />
        ) : (
          <>
            <Box className="item-flex" mt="20px">
              {stats?.map((item, index) => (
                <Box key={item.title} mr="32px" my="8px">
                  <Box className="item-flex" mb="6px">
                    <Box
                      bgcolor={
                        analysisColors.large[
                          index % analysisColors.large.length
                        ]
                      }
                      height="8px"
                      width="12px"
                      borderRadius="2px"
                      mr="4px"
                    />
                    <Box fontSize={12} fontWeight={400} color="#777">
                      {item.title}
                    </Box>
                  </Box>
                  <Box className="item-flex">
                    <Box fontSize={18} fontWeight={700} color="#555" mr="8px">
                      {item?.value?.toLocaleString()}
                    </Box>
                    <Percent
                      data={
                        item?.growth !== "-" ? item?.growth?.toFixed(0) : "-"
                      }
                    />
                  </Box>
                </Box>
              ))}
            </Box>
            <ReactECharts option={option} style={{ height: "300px" }} />
          </>
        )}
      </Box>
    </Box>
  );
}
